import * as React from 'react';
import { Modal, Row, Col, Icon } from 'antd';
import { resolve } from 'inversify-react';

import { TextView } from 'src/core/ui/text.view';
import { FormatService } from 'src/core/services/format.service';
import { ObjectUtils } from 'src/core/utils/object';

interface Props extends ComponentViewProps<AdjustmentModel> {
	costCurrencyFormat: string;
	sellCurrencyFormat: string;
	onOk: (salesAdjustment: SalesAdjustmentProductManually) => void;
	onCancel: () => void;
}

interface State {
	costPrice: number;
	sellPrice: number;
	salesmanProfit: number;
}

export class PriceAdjustmentModal extends React.Component<Props, State> {
	@resolve
	private formatService: FormatService;

	constructor(props: Readonly<Props>) {
		super(props);

		this.state = {
			costPrice: props.model.costPrice,
			sellPrice: props.model.salePriceGross,
			salesmanProfit: props.model.salesmanProfit
		};
	}

	private getProfit(sell: number, cost: number): number {
		return ObjectUtils.round(sell - cost, 2);
	}

	private getMargin(sell: number, cost: number): number {
		return ObjectUtils.round(100 * (sell - cost) / sell);
	}

	private handleChangeCostPrice = (costPrice: number): void => {
		this.setState({ costPrice });
	};

	private handleChangeSellPrice = (sellPrice: number): void => {
		this.setState({ sellPrice });
	};

	private handleChangeSalesmanProfit = (salesmanProfit: number): void => {
		this.setState({ salesmanProfit });
	};

	private handleOk = (): void => {
		const { costPrice, sellPrice, salesmanProfit } = this.state;
		const { model, onOk } = this.props;

		const saleAdjustment: SalesAdjustmentProductManually = {
			quoteId: model.quoteId,
			productDisplayName: 'Price Adjustment',
			productType: 7, // SalesAdjustment
			quoteEntryId: model.quoteEntryId,
			dateFrom: model.dateFrom,
			dateTo: model.dateTo,
			supplierId: model.supplierId,
			supplierTitle: model.supplierTitle,
			occupancies: model.occupancies.map(m => ({ id: m.id, name: '', age: 0 })),
			requiresConfirmation: false,
			costPrice: costPrice,
			costPriceResult: {
				breakdown: [{
					exchangeRate: 1,
					percent: false,
					price: costPrice,
					priceType: 'PerUnit',
					unitPrice: costPrice,
				}],
				paxs: model.occupancies.map(e => e.id),
				exchangeRate: 1,
				isCommissionable: false,
				isPriceLive: true,
				currencyIsoCode: model.costCurrencyCode,
				sourceCurrencyIsoCode: model.costCurrencyCode,
				totalPrice: costPrice
			},
			sellPrice: sellPrice,
			salePriceGross: sellPrice,
			sellPriceResult: {
				breakdown: [{
					exchangeRate: 1,
					percent: false,
					price: sellPrice,
					priceType: 'PerUnit',
					unitPrice: sellPrice,
				}],
				paxs: model.occupancies.map(e => e.id),
				exchangeRate: 1,
				isCommissionable: false,
				isPriceLive: true,
				currencyIsoCode: model.sellCurrencyCode,
				sourceCurrencyIsoCode: model.sellCurrencyCode,
				totalPrice: sellPrice
			},
			salesmanProfit: salesmanProfit,
			productCategory: undefined,
			productServiceType: undefined,
			isRelative: false
		};

		onOk(saleAdjustment);
	};

	render(): React.ReactNode {
		const { costPrice, sellPrice, salesmanProfit } = this.state;
		const { model, costCurrencyFormat, sellCurrencyFormat, onCancel } = this.props;

		return (
			<Modal
				visible
				title="Price Adjustment"
				style={{ top: 20 }}
				width="60%"
				onOk={this.handleOk}
				onCancel={() => onCancel()}>
				<Row gutter={24}>
					<Col xs={16}>
						<div className="display-field">
							<label>{model.supplierTitle} - {model.productDisplayName}</label>
						</div>
						<div className="display-field">
							<Row gutter={16}>
								<Col xs={12}>
									<div>
										<h3>Booking Reference: {model.bookingReference}</h3>
									</div>
									<div>
										<h4>From:</h4> {this.formatService.toString('date', model.dateFrom, 'utc')} {this.formatService.toString('time', model.dateFrom, 'utc')}
									</div>
									<div>
										<h4>To:</h4> {this.formatService.toString('date', model.dateTo, 'utc')} {this.formatService.toString('time', model.dateTo, 'utc')}
									</div>
									<div>
										<h4>Cost:</h4> {this.formatService.toString('currency', ObjectUtils.round(model.costPrice), costCurrencyFormat)}
										{
											ObjectUtils.round(model.costPrice) != ObjectUtils.round(model.calculatedCostPrice) &&
											<span style={{ marginLeft: '2px' }}>({this.formatService.toString('currency', ObjectUtils.round(model.calculatedCostPrice), costCurrencyFormat)})</span>
										}
									</div>
									<div>
										<h4>Sell:</h4> {this.formatService.toString('currency', ObjectUtils.round(model.sellPrice), sellCurrencyFormat)}
									</div>
									<div>
										<h4>Sale Gross:</h4> {this.formatService.toString('currency', ObjectUtils.round(model.salePriceGross), sellCurrencyFormat)}
										{
											ObjectUtils.round(model.calculatedSalePriceGross) != ObjectUtils.round(model.salePriceGross) &&
											<span style={{ marginLeft: '2px' }}>({this.formatService.toString('currency', ObjectUtils.round(model.calculatedSalePriceGross), sellCurrencyFormat)})</span>
										}
									</div>
								</Col>
								<Col xs={12}>
									<div>
										<h4>Clients:</h4>
										{model.clientsDisplayNames.split(', ').map(m => <div key={m}>{m}</div>)}
									</div>
								</Col>
							</Row>
						</div>
					</Col>
					<Col xs={8}>
						<Row gutter={8}>
							<Col xs={12}>
								<TextView
									required
									label="Cost Price"
									mode="edit"
									targetType="float"
									value={costPrice}
									onChanged={this.handleChangeCostPrice}
									suffix={model.costCurrencyCode}
								/>
							</Col>
							<Col xs={12}>
								<TextView
									required
									label="Sell Price"
									mode="edit"
									targetType="float"
									value={sellPrice}
									onChanged={this.handleChangeSellPrice}
									suffix={model.sellCurrencyCode}
								/>
							</Col>
							<Col xs={12}>
								<TextView
									label="Profit"
									mode="display"
									value={this.getProfit(sellPrice, costPrice * model.sellExchangeRate)}
									suffix={model.sellCurrencyCode}
								/>
							</Col>
							<Col xs={12}>
								<TextView
									label="Margin"
									mode="display"
									value={this.getMargin(sellPrice, costPrice * model.sellExchangeRate)}
									suffix="%"
								/>
							</Col>
							<Col xs={12}>
								<TextView
									required
									label="Salesman Profit"
									mode="edit"
									targetType="float"
									value={salesmanProfit}
									onChanged={this.handleChangeSalesmanProfit}
									suffix={model.sellCurrencyCode}
									description={
										salesmanProfit !== this.getProfit(sellPrice, costPrice * model.sellExchangeRate) &&
										<React.Fragment>
											<Icon type="exclamation-circle"  />
											<span style={{ marginLeft: '3px'}}>Mismatch between <b>Salesman Profit</b> and <b>Real Profit</b></span>
										</React.Fragment>
									}
								/>
							</Col>
							<Col xs={12}>
								<TextView
									label="Salesman Margin"
									mode="display"
									value={this.getMargin(costPrice * model.sellExchangeRate + salesmanProfit, costPrice * model.sellExchangeRate)}
									suffix="%"
								/>
							</Col>
						</Row>
					</Col>
				</Row>
			</Modal>
		);
	}
}

export interface AdjustmentModel {
	quoteId: number;
	bookingReference: string;
	quoteEntryId: string;
	productDisplayName: string;
	dateTo: Date;
	dateFrom: Date;
	supplierId: number;
	supplierTitle: string;
	costPrice: number;
	sellPrice: number;
	salePriceGross: number;
	calculatedCostPrice: number;
	calculatedSalePriceGross: number;
	sellExchangeRate: number;
	salesmanProfit: number;
	costCurrencyCode: string;
	sellCurrencyCode: string;
	occupancies: QuoteEntryOccupancy[];
	clientsDisplayNames: string;
}



// WEBPACK FOOTER //
// ./src/app/accounting/shared/price-adjustment/price-adjustment-modal.tsx